import { createSelector } from 'reselect';
import { difference, identity } from 'lodash';

import { deepPick } from 'utils/deep-pick';
import { createDeepEqualSelector } from 'utils/create-deep-equal-selector';

import { UNIT_ARRAY, UNIT_OBJECT } from 'constants/unit';
import { compliantStageDeclaration, compliantOutput } from './compliance-utils';

/**
 * This function wraps a process spec and data stage with several checks intended
 * to allow the stages to be run lazily. If what a stage listens to does not change,
 * the output will be what was calculated previously. This is necessary for our charts,
 * since we make use of pure components / reference equality to prevent unnecessary rerenders.
 *
 * Makes some checks to make sure the stage we're using is compliant with the new format:
 * - Has an array of inputs for the stage, ie what it will listen on
 * - Checks to prevent the stage from returning values it hasn't changed / couldn't have changed
 *
 * @param {Function} stageFn The processSpecAndData stage function
 * @returns {Function} A wrapped version of the processSpecAndData stage
 */
export function cacheStageData(stageFn) {
  if (__DEV__) {
    compliantStageDeclaration(stageFn);
  }

  // any other state is going to be in the environment etc, so it's going to be pretty small / easy to deep compare
  const cheapStateKeys = difference(stageFn.input, ['documents', 'cache']);
  const cheapStateSelector = createDeepEqualSelector(
    state => deepPick(state, cheapStateKeys),
    identity
  );

  // optimisation: Prevent the cheap reference checking from responding to cache / document changes if not needed
  const hasDocuments = stageFn.input.includes('documents');
  const hasCache = stageFn.input.includes('cache');

  const fnSelector = createSelector(
    cheapStateSelector,
    state => (hasDocuments ? state.documents : UNIT_ARRAY),
    state => (hasCache ? state.cache : UNIT_OBJECT),
    (cheapState, documents, cache) => {
      return stageFn.fn({ ...cheapState, documents, cache });
    }
  );

  return stageData => {
    const output = fnSelector(stageData);

    if (__DEV__) {
      compliantOutput(stageFn, output);
    }
    return { ...stageData, ...output };
  };
}
